'use client';

import React from 'react';
import Image from 'next/image';
import { Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';

const GOLD = '#faaf40';

// --- CUSTOM ICONS ---

// 1. TikTok Icon
const TikTokIcon = ({ size = 20, className }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

// 2. X (Twitter) Icon
const XIcon = ({ size = 20, className }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// 3. WhatsApp Icon
const WhatsAppIcon = ({ size = 20, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const footerLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Membership Plans', path: '/membershipPlans' },
  { name: 'Diet Plans', path: '/dietplans' },
  { name: 'Transformations', path: '/transformations' },
  { name: 'Psychology', path: '/psychology' },
  { name: 'Blogs', path: '/blogs' },
];

export default function Footer() {
  return (
    <footer className="relative bg-gradient-to-r from-[#1f1f1f] via-[#2c2c2c] to-[#3a2f21] border-t border-[#faaf40] text-gray-300">
      
      {/* Main Grid: Left Content | Right Map */}
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[500px]">
        
        {/* --- LEFT HALF: Content & Links --- */}
        <div className="p-8 lg:p-16 flex flex-col justify-center space-y-10 order-2 lg:order-1">
          
          {/* Logo, Description & Socials */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Image
                src="/logoWhite.png"
                alt="Aura Wellness Clubs"
                width={60}
                height={60}
                className="object-contain"
              />
              <h2 className="text-2xl font-extrabold tracking-wide text-white uppercase">
                <span style={{ color: GOLD }}>Aura</span> Wellness Clubs
              </h2>
            </div>
            
            <p className="text-sm leading-relaxed text-gray-400 max-w-md">
              Redefining wellness through innovation, balance, and the science of vitality.
            </p>

            {/* Social Media Icons */}
            <div className="flex gap-4 flex-wrap">
              <a href="https://www.instagram.com/aurawellnessclubs/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-600 hover:border-[#faaf40] hover:text-[#faaf40] transition-all duration-300 hover:scale-110">
                <Instagram size={20} />
              </a>
              <a href="https://www.facebook.com/aurawellnessclub" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-600 hover:border-[#faaf40] hover:text-[#faaf40] transition-all duration-300 hover:scale-110">
                <Facebook size={20} />
              </a>
              <a href="https://www.tiktok.com/@aurafit_12" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-600 hover:border-[#faaf40] hover:text-[#faaf40] transition-all duration-300 hover:scale-110">
                <TikTokIcon size={20} />
              </a>
              <a href="https://www.linkedin.com/in/aura-wellness-clubs-580442376/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-600 hover:border-[#faaf40] hover:text-[#faaf40] transition-all duration-300 hover:scale-110">
                <Linkedin size={20} />
              </a>
              <a href="http://youtube.com/@aurawellnessclubs" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-600 hover:border-[#faaf40] hover:text-[#faaf40] transition-all duration-300 hover:scale-110">
                <Youtube size={20} />
              </a>
              
              {/* X (Twitter) Link */}
              <a href="https://x.com/AuraFit_12" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-600 hover:border-[#faaf40] hover:text-[#faaf40] transition-all duration-300 hover:scale-110">
                <XIcon size={20} />
              </a>

              {/* WhatsApp Link */}
              <a href="https://wa.me/923460442929" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-600 hover:border-[#faaf40] hover:text-[#faaf40] transition-all duration-300 hover:scale-110">
                <WhatsAppIcon size={20} />
              </a>
            </div>
          </div>

          {/* Links & Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {/* Navigation */}
            <div>
              <h3 className="text-lg font-semibold mb-4 uppercase tracking-wide" style={{ color: GOLD }}>
                Explore
              </h3>
              <ul className="space-y-2 text-sm">
                {footerLinks.map((link) => (
                  <li key={link.name}>
                    <a href={link.path} className="hover:text-[#faaf40] transition-colors duration-200 block">
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4 uppercase tracking-wide" style={{ color: GOLD }}>
                Visit Us
              </h3>
              <div className="space-y-4 text-sm text-gray-400">
                <div className="flex items-start gap-3">
                  <span className="text-[#faaf40] mt-1">📍</span>
                  <p>
                    1st Floor, SRB Building,<br />
                    F-6 Markaz, Islamabad,<br />
                    Pakistan
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#faaf40]">📞</span>
                  <a href="tel:+923460442929" className="hover:text-[#faaf40] transition">+92 346 044 2929</a>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#faaf40]">✉️</span>
                  <a href="mailto:info@aurawellness.com" className="hover:text-[#faaf40] transition">info@aurawellness.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- RIGHT HALF: Google Map --- */}
        <div className="relative w-full h-[300px] lg:h-auto min-h-full border-t lg:border-t-0 lg:border-l border-[#faaf40] order-1 lg:order-2">
           {/* Dark Mode Filter on Map */}
           <iframe 
             width="100%" 
             height="100%" 
             frameBorder="0" 
             scrolling="no" 
             marginHeight="0" 
             marginWidth="0" 
             title="Aura Wellness Location"
             src="https://maps.google.com/maps?q=SRB+Business+Center+Islamabad&t=&z=13&ie=UTF8&iwloc=&output=embed"
             style={{ filter: 'grayscale(1) contrast(1.2) brightness(0.8)' }}
             className="w-full h-full absolute inset-0"
           >
           </iframe>
           
           {/* Gold Overlay Effect */}
           <div className="absolute inset-0 bg-[#faaf40]/5 pointer-events-none mix-blend-overlay"></div>
        </div>
      </div>

      {/* --- BOTTOM BAR: Full Width Copyright --- */}
      <div className="w-full border-t border-[#2c2c2c] py-6 text-center text-sm text-gray-500 bg-black/70 backdrop-blur-sm relative z-20">
        © {new Date().getFullYear()} Aura Wellness Clubs — All Rights Reserved.
      </div>

    </footer>
  );
}